export const base64ToBlob = (base64, type) => {
  const binaryString = window.atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new Blob([bytes], { type: type });
};

export const handleDownload = async (url, filename) => {
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Download failed:', error);
  }
};

// Fetches remote content into a same-origin blob URL so the print iframe's
// contentWindow (and its afterprint event) stays accessible across browsers.
// Firefox throws a SecurityError when accessing contentWindow of a
// cross-origin iframe, which breaks printing of remote (e.g. S3) files there.
const toSameOriginUrl = async (url) => {
  if (url.startsWith('blob:')) {
    return { url, createdBlobUrl: null };
  }
  try {
    // Blob conversion only exists so a cross-origin iframe stays printable.
    // Same-origin files can be printed from their own URL.
    if (new URL(url, window.location.href).origin === window.location.origin) {
      return { url, createdBlobUrl: null };
    }
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to load print content (HTTP ${response.status})`);
    }
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    return { url: objectUrl, createdBlobUrl: objectUrl };
  } catch (error) {
    // CORS or network failure: fall back to the direct URL. Content may end up
    // cross-origin there, in which case print() fails fast below instead of
    // hanging.
    console.warn('Could not load print content as same-origin blob:', error);
    return { url, createdBlobUrl: null };
  }
};

const PRINT_READY = 'fleetenable:print:ready';
const PRINT_ERROR = 'fleetenable:print:error';
const imageExtensionPattern = /\.(jpg|jpeg|png|gif)$/i;

const isImageUrl = (url) =>
  imageExtensionPattern.test(String(url).split('?')[0].split('#')[0]);

const escapeHtmlAttr = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const setupPrintFrame = async (url, { rejectOnError = false, supportImages = false } = {}) => {
  const { url: targetUrl, createdBlobUrl } = await toSameOriginUrl(url);
  // Decided from the original URL so images keep their centering/scaling
  // wrapper even after being converted to a same-origin blob URL.
  const isImage = supportImages && isImageUrl(url);

  return new Promise((resolve, reject) => {
    let iframe = null;
    let fallbackTimer = null;
    let messageHandler = null;
    let cleanedUp = false;
    let settled = false;
    let printTriggered = false;
    let watchDialog = false;
    let printWindowBlurred = false;
    let observedPrintWindow = null;
    let onAfterPrint = null;
    let onWindowBlur = null;
    let onWindowFocus = null;
    let onPageHide = null;

    // Resolves the caller's promise without touching the iframe. Removing the
    // iframe while the print preview is open closes the dialog.
    const settle = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(fallbackTimer);
      if (error && rejectOnError) {
        reject(error);
      } else {
        resolve();
      }
    };

    const detachDialogObservers = () => {
      if (observedPrintWindow && onAfterPrint) {
        try {
          observedPrintWindow.removeEventListener('afterprint', onAfterPrint);
        } catch (securityError) {
          // contentWindow is already gone with the iframe.
        }
      }
      if (onWindowBlur) window.removeEventListener('blur', onWindowBlur);
      if (onWindowFocus) window.removeEventListener('focus', onWindowFocus);
      if (onPageHide) window.removeEventListener('pagehide', onPageHide);
      observedPrintWindow = null;
      onAfterPrint = null;
      onWindowBlur = null;
      onWindowFocus = null;
      onPageHide = null;
    };

    // Revokes the blob URL, drops listeners, and removes the iframe.
    // This is the only cleanup path, including the 15s fallback.
    const finish = (error) => {
      if (cleanedUp) return;
      cleanedUp = true;
      clearTimeout(fallbackTimer);
      detachDialogObservers();
      if (messageHandler) {
        window.removeEventListener('message', messageHandler);
        messageHandler = null;
      }
      if (createdBlobUrl) {
        window.URL.revokeObjectURL(createdBlobUrl);
      }
      if (iframe && document.body.contains(iframe)) {
        document.body.removeChild(iframe);
        iframe = null;
      }
      settle(error);
    };

    const failFast = (error) => {
      console.error('Print failed:', error);
      finish(error);
    };

    // Opens the print dialog and cleans up on afterprint. All error paths
    // (cross-origin contentWindow, blocked print) finalize immediately so the
    // caller never waits out the safety timer and the iframe can't leak.
    const triggerPrint = () => {
      if (cleanedUp || printTriggered) return;
      printTriggered = true;
      try {
        const printWindow = iframe.contentWindow;
        // afterprint covers Firefox. Chrome's PDF viewer often never emits it;
        // that dialog blurs this window and focuses it again on close, which
        // is the other path into finish(). Events from focus()/print() itself
        // are ignored until watchDialog is set.
        observedPrintWindow = printWindow;
        onAfterPrint = () => finish();
        onWindowBlur = () => {
          if (watchDialog) printWindowBlurred = true;
        };
        onWindowFocus = () => {
          if (!watchDialog || !printWindowBlurred) return;
          finish();
        };
        onPageHide = () => finish();
        try {
          printWindow.addEventListener('afterprint', onAfterPrint);
        } catch (securityError) {
          // Cross-origin iframe: afterprint can't be observed here.
          console.warn('Cannot observe afterprint on this content:', securityError);
        }
        window.addEventListener('blur', onWindowBlur);
        window.addEventListener('focus', onWindowFocus);
        window.addEventListener('pagehide', onPageHide);
        printWindow.focus();
        printWindow.print();
        watchDialog = true;
      } catch (error) {
        // Cross-origin content: print() throws SecurityError and the file can't
        // be printed via an iframe. Fail fast instead of waiting 15s.
        failFast(error);
      }
    };

    try {
      iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);

      messageHandler = (event) => {
        if (event.source !== iframe.contentWindow) return;
        if (event.data === PRINT_READY) {
          triggerPrint();
        } else if (event.data === PRINT_ERROR) {
          failFast(new Error('Failed to load print content'));
        }
      };
      window.addEventListener('message', messageHandler);

      // Same cleanup the review asked for (revoke, remove listener, remove
      // iframe). Running it while Firefox's preview is still open closes that
      // preview, so finish() runs here only when nothing is on screen or the
      // Chrome dialog has already gone without afterprint. An open preview
      // stays up; afterprint, dialog focus, or leaving the page still calls finish().
      fallbackTimer = setTimeout(() => {
        if (cleanedUp) return;
        if (!printTriggered || (printWindowBlurred && document.hasFocus())) {
          finish(printTriggered ? undefined : new Error('Print did not complete'));
          return;
        }
        console.warn('Print dialog still open; unblocking caller.');
        if (messageHandler) {
          window.removeEventListener('message', messageHandler);
          messageHandler = null;
        }
        settle();
      }, 15000);

      iframe.onerror = () => failFast(new Error('Failed to load print content'));

      if (isImage) {
        // srcdoc keeps the iframe same-origin (image loads are signalled by
        // the inner <img> load/error events so we never print a blank frame).
        iframe.srcdoc = `
          <html>
            <head>
              <style>
                body { margin: 0; display: flex; justify-content: center; align-items: center; }
                img { max-width: 100%; max-height: 100vh; object-fit: contain; }
              </style>
            </head>
            <body>
              <img id="print-image" src="${escapeHtmlAttr(targetUrl)}" />
              <script>
                (function () {
                  var img = document.getElementById('print-image');
                  img.onload = function () { window.parent.postMessage('${PRINT_READY}', '*'); };
                  img.onerror = function () { window.parent.postMessage('${PRINT_ERROR}', '*'); };
                  if (img.complete) {
                    if (img.naturalWidth > 0) {
                      window.parent.postMessage('${PRINT_READY}', '*');
                    } else {
                      window.parent.postMessage('${PRINT_ERROR}', '*');
                    }
                  }
                })();
              <\/script>
            </body>
          </html>
        `;
      } else {
        iframe.onload = () => {
          // fire load — file is already fetched into a blob (or same-origin)
          // so the viewer has content; print directly without an arbitrary
          // render delay.
          triggerPrint();
        };
        iframe.src = targetUrl;
      }
    } catch (error) {
      console.error('Print setup failed:', error);
      finish(error);
    }
  });
};

export const handlePrint = async (url) => setupPrintFrame(url);

export const downloadFile = async (url, filename) => {
  try {
    if (url.startsWith('blob:')) {
      // Handle blob URLs
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // Handle regular URLs
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }
  } catch (error) {
    console.error('Download failed:', error);
  }
};

export const printFile = async (url) => setupPrintFrame(url, { rejectOnError: true, supportImages: true });
